import React from 'react';

interface RelativeScale {
    scale: number;
    label: string;
    flops: string;
  }

export default function CompanyCompute() {
  // Configuration variables for precise positioning
  const config = {
    // Canvas dimensions
    canvas: {
      width: 610,
      height: 300,
      padding: 10
    },
    
// Growth metrics positioning
metrics: {
    x: 100,
    y: 10,
    width: 300,
    height: 80,
    
    // Vertical lines configuration
    lines: [
      {
        x: 230,
        color: '#0000AA', // orange-400 (efficiency color)
        width: 2
      },
      {
        x: 170,
        color: '#AA0000', // yellow-400 (production color)
        width: 2
      }
    ],
    
    // Labels configuration
    labels: {
      efficiency: {
        name: "global compute",
        value: "2.25x",
        nameX: 135, // position for the name (left side)
        nameY: 25,
        valueX: 120, // position for the value (right side)
        valueY: 0,
        nameColor: '#374151', // gray-700
        valueColor: '#AA0000' // orange-500
      },
      production: {
        name: "share of global",
        value: "1.5x",
        nameX: -40,
        nameY: 24,
        valueX: 235,
        valueY: 0,
        nameColor: '#374151', // gray-700
        valueColor: '#0000AA' // yellow-500
      }
    }
  },
    
    // Global AI compute box positioning
    globalCompute: {
      x: 300, // centered
      y: 80,
      width: 320,
      height: 100,
      borderRadius: 8
    },
    
    // Visualization area
    visualization: {
      startX: 80,
      y: 220,
      width: 440,
      boxSize: 8,
      boxGap: 1,
      boxRadius: 1,
      periodSpacing: 460 // distance between the two time periods
    },
    
    // Legend
    legend: {
      x: 300, // centered
      y: 220
    }
  };

  // Define scaling factor and box size
  const boxSize = 0.2; // 0.1 means each box represents 1M H100e
  
  // Define two time periods with their relative scales
const relativeScales: RelativeScale[] = [
    {scale: 1, label: 'Dec 2024\n500K H100e', flops: '500K H100e'}, 
    {scale: 40, label: 'Dec 2027\n20M H100e', flops: '20M H100e'}
  ];
  
  // Growth metrics
  const growthData = {
    efficiency: 2.25,
    production: 1.5,
    compound: 3.4 // per year
  };

  // Function to generate grid boxes for visualization
  function renderBoxGrid(scale: number, xPosition: number) {
    const boxes = [];
    const gridDimension = Math.floor(Math.sqrt(scale / boxSize));
    
    // Calculate starting position to center the grid
    const gridWidth = gridDimension * (config.visualization.boxSize + config.visualization.boxGap);
    const startingX = xPosition - (gridWidth / 2);
    
    for (let row = 0; row < gridDimension; row++) {
      for (let col = 0; col < gridDimension; col++) {
        const boxX = startingX + col * (config.visualization.boxSize + config.visualization.boxGap);
        const boxY = config.visualization.y - (gridDimension * (config.visualization.boxSize + config.visualization.boxGap)) + 
                    row * (config.visualization.boxSize + config.visualization.boxGap);
        
        boxes.push(
          <div 
            key={`box-${row}-${col}`} 
            style={{
              position: 'absolute',
              left: boxX,
              top: boxY,
              width: config.visualization.boxSize,
              height: config.visualization.boxSize,
              backgroundColor: '#374151', // gray-700
              borderRadius: config.visualization.boxRadius,
              transition: 'background-color 500ms'
            }}
            className="hover:bg-blue-500"
          />
        );
      }
    }
    
    // Add label
    boxes.push(
      <div 
        key={`label-${scale}`}
        style={{
          position: 'absolute',
          left: xPosition - 40,
          top: config.visualization.y + 20,
          width: 80,
          textAlign: 'center',
          fontSize: '0.75rem',
        }}
      >
        {relativeScales.find(item => item.scale === scale)?.label || 'Unknown'}
      </div>
    );
    
    return boxes;
  };

  return (
    <div style={{ 
      position: 'relative', 
      width: config.canvas.width,
      height: config.canvas.height,
      maxWidth: '100%',
      overflowX: 'auto',
      margin: '32px auto',
      fontFamily: 'monospace',
    }}>
    
      {/* Growth metrics with fully configurable vertical lines and labels */}
      <div style={{ position: 'absolute', left: config.metrics.x, top: config.metrics.y, width: config.metrics.width }}>
        {/* Vertical lines - fully configurable */}
        {config.metrics.lines.map((line, index) => (
          <div 
            key={`line-${index}`}
            style={{
              position: 'absolute',
              left: line.x,
              top: 0,
              width: line.width,
              height: config.metrics.height,
              backgroundColor: line.color,
            }}
          ></div>
        ))}
        
        {/* Efficiency label - fully configurable */}
        <div style={{ 
          position: 'absolute',
          right: config.metrics.labels.efficiency.nameX,
          top: config.metrics.labels.efficiency.nameY,
          textAlign: 'right',
          fontSize: '0.75rem',
          color: config.metrics.labels.efficiency.nameColor
        }}>
          {config.metrics.labels.efficiency.name}
        </div>
        
        {/* Efficiency value - fully configurable */}
        <div style={{ 
          position: 'absolute',
          left: config.metrics.labels.efficiency.valueX,
          top: config.metrics.labels.efficiency.valueY,
          fontWeight: 500,
          color: config.metrics.labels.efficiency.valueColor
        }}>
          {growthData.efficiency}x
        </div>
        
        {/* Production label - fully configurable */}
        <div style={{ 
          position: 'absolute',
          right: config.metrics.labels.production.nameX,
          top: config.metrics.labels.production.nameY,
          textAlign: 'right',
          fontSize: '0.75rem',
          color: config.metrics.labels.production.nameColor
        }}>
          {config.metrics.labels.production.name}
        </div>
        
        {/* Production value - fully configurable */}
        <div style={{ 
          position: 'absolute',
          left: config.metrics.labels.production.valueX,
          top: config.metrics.labels.production.valueY,
          fontWeight: 500,
          color: config.metrics.labels.production.valueColor
        }}>
          {growthData.production}x
        </div>
      </div>

      {/* Global AI compute box */}
      <div style={{ 
        position: 'absolute',
        left: config.globalCompute.x - (config.globalCompute.width / 2),
        top: config.globalCompute.y,
        width: config.globalCompute.width,
        height: config.globalCompute.height,
        backgroundColor: '#f3f4f6', // gray-100
        borderRadius: config.globalCompute.borderRadius,
        padding: '16px',
        textAlign: 'center'
      }}>
        <div style={{ color: '#4b5563', fontWeight: 500 }}>Leading AI Company compute</div>
        <div style={{ color: '#055609', fontSize: '1.5rem', fontWeight: 600 }}>{growthData.compound}x/year</div>
        <div style={{ fontSize: '0.75rem', marginTop: '4px' }}>Compound effect of global compute × share</div>
      </div>
      
      {/* Visualization boxes for first time period */}
      {renderBoxGrid(relativeScales[0].scale, config.visualization.startX)}
      
      {/* Visualization boxes for second time period */}
      {renderBoxGrid(relativeScales[1].scale, config.visualization.startX + config.visualization.periodSpacing)}
      
      {/* Legend */}
      <div style={{ 
        position: 'absolute',
        left: config.legend.x - 60,
        top: config.legend.y,
        width: 120,
        textAlign: 'center',
        fontSize: '0.75rem'
        }}>
        <div style={{ 
            display: 'inline-block', 
            width: config.visualization.boxSize, 
            height: config.visualization.boxSize, 
            backgroundColor: '#374151', 
            borderRadius: config.visualization.boxRadius,
            marginRight: 5,
            verticalAlign: 'middle' 
        }}></div> = 125K H100e
        </div>
    </div>
  );
}